import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";
import { caseQuickUpdate, caseQuickUpdateMass } from "../../../../lib/fetchApi";
import { formatCurrency, makeTotals, returnPractice } from "../../../../lib/script";
import DefaultUpdater from "../../../common/DefaultUpdater";
import InputBoxCurrency from "../../../common/interactive/InputBoxCurrency";
import DisplayBoxRight from "../../../fragments/DisplayBoxRight";
import { notificationActions } from "../../../../store/notifications";
import { caseActions } from "../../../../store/case";
import classes from './TotalsTable.module.css'
import { miscellaneous } from "../../../../lib/misc";
import HeaderCellGroup from "../../../work_queues/cells/HeaderCellGroup";
import { shouldShowFieldUpdateLoader } from "../../../../lib/systemData";

function getPracticeCellPendingKey(typeId, field) {
    return `case_practice:${typeId}:${field}`
}

export default function TotalsTable({practices}) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase)
    const [pendingCells, setPendingCells] = useState({})
    const showApiLoader = shouldShowFieldUpdateLoader()

    const isPracticeCellPending = (typeId, field) =>
        (pendingCells[getPracticeCellPendingKey(typeId, field)] ?? 0) > 0
    const primaryUmInsurance = useMemo(() => {
        if (!activeCase?.mp_pip_um?.length) {
            return null
        }
        return activeCase.mp_pip_um.find((item) => item?.is_um_primary_insurance) || activeCase.mp_pip_um[0]
    }, [activeCase])
    const isUmSplitEnabled = Boolean(primaryUmInsurance?.um_neg_split)
    const hasLiaNegBalance = useMemo(() => (practices ?? []).some((item) => item.lia_neg_amount_balance), [practices])
    const hasUmNegBalance = useMemo(() => (practices ?? []).some((item) => item.um_neg_amount_balance), [practices])
    const totalCharges = useMemo(() => makeTotals(practices, 'total_charges'), [practices])
    const totalBalance = useMemo(() => makeTotals(practices, 'balance'), [practices])
    const totalLiaOfferedAmount = useMemo(() => makeTotals(practices, 'lia_neg_offered_amount'), [practices])
    const totalLiaCounterOffer = useMemo(() => makeTotals(practices, 'lia_neg_counter_offer'), [practices])
    const totalLiaNegAmount = useMemo(() => makeTotals(practices, 'lia_neg_amount'), [practices])
    const totalLiaReceived = useMemo(() => makeTotals(practices, 'lia_neg_received_amount'), [practices])
    const totalUmOfferedAmount = useMemo(() => makeTotals(practices, 'um_neg_offered_amount'), [practices])
    const totalUmCounterOffer = useMemo(() => makeTotals(practices, 'um_neg_counter_offer'), [practices])
    const totalUmNegAmount = useMemo(() => makeTotals(practices, 'um_neg_amount'), [practices])
    const totalUmReceived = useMemo(() => makeTotals(practices, 'um_neg_received_amount'), [practices])

    const hasAmountValue = (value) => value != null && value !== ''

    const toNumberOrNull = (value) => {
        if (value == null || value === '') return null
        const n = Number(`${value}`.replace(/,/g, ''))
        return Number.isFinite(n) ? n : null
    }

    const formatPercentageOrNull = (totalAmount, amount) => {
        const total = toNumberOrNull(totalAmount)
        const part = toNumberOrNull(amount)
        if (total == null || total === 0 || part == null) return null
        return ((part * 100) / total).toFixed(2)
    }

    const getLiabilityPctBaseAmount = (practice) => {
        if (hasAmountValue(practice?.lia_neg_amount)) return practice.lia_neg_amount
        return practice?.lia_neg_offered_amount
    }

    const getUmPctBaseAmount = (practice) => {
        if (hasAmountValue(practice?.um_neg_amount)) return practice.um_neg_amount
        return practice?.um_neg_offered_amount
    }

    const totalLiaPctBaseAmount = useMemo(() => {
        return (practices || []).reduce((sum, item) => {
            const n = toNumberOrNull(getLiabilityPctBaseAmount(item))
            return n == null ? sum : sum + n
        }, 0)
    }, [practices])

    const totalUmPctBaseAmount = useMemo(() => {
        return (practices || []).reduce((sum, item) => {
            const n = toNumberOrNull(getUmPctBaseAmount(item))
            return n == null ? sum : sum + n
        }, 0)
    }, [practices])

    const roundCurrencyValue = (value) => {
        if (value == null || value === '') return value
        const n = Number(value)
        if (Number.isNaN(n)) return value
        return n.toFixed(2)
    }

    function calculateAmountPercent(totalAmount, amount) {

        if (totalAmount && amount) {
            return (amount*100)/totalAmount
        }

        return null
    }

    async function practiceCellUpdateHandler(data) {
        const field = data?.identifier?.field
        const typeId = data?.identifier?.type_id
        const folder = data?.identifier?.folder ?? 'practices'

        if (!field || typeId == null || !activeCase?.case?.id) {
            return
        }

        const practiceItem = (practices || []).find((p) => p.id === typeId)
        const originalValue = practiceItem?.[field] ?? null
        const newValue = data?.data?.value ?? null
        const cellKey = getPracticeCellPendingKey(typeId, field)

        setPendingCells((prev) => ({
            ...prev,
            [cellKey]: (prev[cellKey] ?? 0) + 1,
        }))

        dispatch(caseActions.modifyActiveCaseMultiple({
            folder,
            item: {
                itemId: typeId,
                fields: [{ field, value: newValue }],
            },
        }))

        const reqBody = {
            type: data.identifier.type,
            type_id: typeId,
            fields: {
                [field]: newValue,
            },
        }

        try {
            const update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody)
            if (update && update.errorMessage) {
                dispatch(caseActions.modifyActiveCaseMultiple({
                    folder,
                    item: {
                        itemId: typeId,
                        fields: [{ field, value: originalValue }],
                    },
                }))
                dispatch(notificationActions.setError(update))
            }
        } finally {
            setPendingCells((prev) => {
                const next = { ...prev }
                const count = (next[cellKey] ?? 1) - 1
                if (count <= 0) {
                    delete next[cellKey]
                } else {
                    next[cellKey] = count
                }
                return next
            })
        }
    }

    function renderPracticeCurrencyCell(item, field, name, { umDisabled = false } = {}) {
        const identifier = {
            type: 'case_practice',
            permissionType: 'case_negotiation',
            field,
            type_id: item?.id,
            folder: 'practices',
        }
        const apiDisabled = isPracticeCellPending(item?.id, field)

        return (
            <InputBoxCurrency
                action={practiceCellUpdateHandler}
                currentValue={item[field]}
                name={name}
                identifier={identifier}
                textRight={true}
                fixWidth={150}
                hasFieldAppearance={true}
                isNotModifiable={umDisabled ? !isUmSplitEnabled : false}
                apiDisabled={apiDisabled}
                showApiLoader={showApiLoader}
            />
        )
    }

    async function totalsNegotiatedAmountHandler(data) {
        if (!data?.data?.value) {
            return
        }

        const field = data?.identifier?.field
        if (!field) {
            return
        }

        const totalBalanceForSplit = makeTotals(practices, 'balance')

        // Snapshot current values so we can revert on API failure.
        const originalValuesByPracticeId = new Map((practices || []).map((p) => [p.id, p?.[field] ?? null]))

        // Build the mass payload and optimistically update Redux immediately.
        const casePractices = (practices || []).map((practiceItem) => {
            const computedValue = practices.length === 1
                ? data.data.value
                : ((Number(data.data.value) * calculateAmountPercent(totalBalanceForSplit, practiceItem.balance)) / 100).toFixed(2)

            dispatch(caseActions.modifyActiveCaseMultiple({
                folder: data.identifier.folder,
                item: {
                    itemId: practiceItem.id,
                    fields: [
                        { field, value: computedValue }
                    ]
                }
            }))

            return {
                id: practiceItem.id,
                fields: {
                    [field]: computedValue
                }
            }
        })

        const reqBody = { case_practices: casePractices }
        const update = await caseQuickUpdateMass(userToken, activeCase.case.id, reqBody)
        if (update && update.errorMessage) {
            // Revert optimistic changes.
            for (const practiceItem of practices || []) {
                const originalValue = originalValuesByPracticeId.get(practiceItem.id) ?? null
                dispatch(caseActions.modifyActiveCaseMultiple({
                    folder: data.identifier.folder,
                    item: {
                        itemId: practiceItem.id,
                        fields: [
                            { field, value: originalValue }
                        ]
                    }
                }))
            }
            dispatch(notificationActions.setError(update))
        }
    }
    
    return <>
        <table className='table-border-radius-8'>
            <thead>
                <tr>
                    <th colSpan={4}>
                        <HeaderCellGroup item={{groupName: 'Practice'}}/>
                    </th>
                    <th colSpan={hasLiaNegBalance ? 7 : 6}>
                        <HeaderCellGroup item={{groupName: 'Liability'}}/>
                    </th>
                    <th colSpan={hasUmNegBalance ? 7 : 6}>
                        <HeaderCellGroup item={{groupName: 'UM'}}/>
                    </th>
                </tr>
                <tr>
                    <th>Practice ID</th>
                    <th>Chart Number</th>
                    <th>Total Charges</th>
                    <th>Balance</th>

                    {/* liability */}
                    <th>Offered Amount</th>
                    <th>Counter Offer</th>
                    <th>Accepted Amount</th>
                    <th>% of Total</th>
                    {hasLiaNegBalance ? <th>Balance at Neg.</th> : null}
                    <th>Paid</th>
                    <th>Received Amt.</th>

                    {/* um */}
                    <th className={!isUmSplitEnabled ? classes.disabled_header : ''}>Offered Amount</th>
                    <th className={!isUmSplitEnabled ? classes.disabled_header : ''}>Counter Offer</th>
                    <th>Accepted Amount</th>
                    <th>% of Total</th>
                    {hasUmNegBalance ? <th>Balance at Neg.</th> : null}
                    <th>Paid</th>
                    <th>Received Amt.</th>
                </tr>
            </thead>
            <tbody>
                {practices && practices.map(item => (
                    <tr key={item.practice_id}>
                        {/* practice name  */}
                        <td>{returnPractice(item?.practice_id)?.short_name ? returnPractice(item?.practice_id)?.short_name : returnPractice(item?.practice_id)?.name}</td>

                        {/* chart number  */}
                        <td>{item.chart_number}</td>

                        {/* total charges  */}
                        <td><DisplayBoxRight>{item?.total_charges ? <>${formatCurrency(item?.total_charges)}</> : '-'}</DisplayBoxRight></td>

                        {/* balance  */}
                        <td><DisplayBoxRight>{item?.balance ? <>${formatCurrency(item?.balance)}</> : '-'}</DisplayBoxRight></td>

                        {/* liability offered amount  */}
                        <td className="table-cell-fit">
                            {renderPracticeCurrencyCell(item, 'lia_neg_offered_amount', 'lia_neg_offered_amount')}
                        </td>

                        {/* liability counter offer  */}
                        <td className="table-cell-fit">
                            {renderPracticeCurrencyCell(item, 'lia_neg_counter_offer', 'lia_neg_counter_offer')}
                        </td>

                        {/* liability accepted amount  */}
                        <td className="table-cell-fit">
                            {renderPracticeCurrencyCell(item, 'lia_neg_amount', 'lia_neg_amount')}
                        </td>

                        {/* liability percentage of total (negotiated if present, else offered) */}
                        <td>
                            {hasAmountValue(getLiabilityPctBaseAmount(item))
                                ? <DisplayBoxRight>
                                    {formatPercentageOrNull(item.total_charges, getLiabilityPctBaseAmount(item))
                                        ? <>{formatPercentageOrNull(item.total_charges, getLiabilityPctBaseAmount(item))}%</>
                                        : '-'}
                                </DisplayBoxRight>
                                : <DisplayBoxRight>-</DisplayBoxRight>}
                        </td>

                        {/* liability negotiated amount balance  */}
                        {hasLiaNegBalance ? <td className={miscellaneous.areValuesDifferent(item.lia_neg_amount_balance, item.balance) ? 'border_red' : ''}>
                            {item.lia_neg_amount_balance ? <DisplayBoxRight>{item?.lia_neg_amount_balance ? <>${formatCurrency(item?.lia_neg_amount_balance)}</> : '-'}</DisplayBoxRight> : ''}                        
                        </td> : null}

                        {/* liability negotiated amount paid  */}
                        <td>
                            <DefaultUpdater type={'checkbox'} value={item.lia_neg_paid} name={'lia_neg_paid'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'lia_neg_paid', type_id: item?.id, folder: 'practices'}} extraOptions={{setStyle: 'centered'}}/>
                        </td>

                        {/* liability negotiated amount received amount  */}
                        <td><DisplayBoxRight>{item?.lia_neg_received_amount ? <>${formatCurrency(item?.lia_neg_received_amount)}</> : '-'}</DisplayBoxRight></td>

                        {/* um offered amount  */}
                        <td className={`table-cell-fit ${!isUmSplitEnabled ? classes.disabled_cell : ''}`}>
                            {renderPracticeCurrencyCell(item, 'um_neg_offered_amount', 'um_neg_offered_amount', { umDisabled: true })}
                        </td>

                        {/* um counter offer  */}
                        <td className={`table-cell-fit ${!isUmSplitEnabled ? classes.disabled_cell : ''}`}>
                            {renderPracticeCurrencyCell(item, 'um_neg_counter_offer', 'um_neg_counter_offer', { umDisabled: true })}
                        </td>

                        {/* um accepted amount  */}
                        <td className={`table-cell-fit ${!isUmSplitEnabled ? classes.disabled_cell : ''}`}>
                            {renderPracticeCurrencyCell(item, 'um_neg_amount', 'um_neg_amount', { umDisabled: true })}
                        </td>

                        {/* um percentage of total (negotiated if present, else offered) */}
                        <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}>
                            {hasAmountValue(getUmPctBaseAmount(item))
                                ? <DisplayBoxRight>
                                    {formatPercentageOrNull(item.total_charges, getUmPctBaseAmount(item))
                                        ? <>{formatPercentageOrNull(item.total_charges, getUmPctBaseAmount(item))}%</>
                                        : '-'}
                                </DisplayBoxRight>
                                : <DisplayBoxRight>-</DisplayBoxRight>}
                        </td>

                        {/* um negotiated amount balance  */}
                        {hasUmNegBalance ? <td className={miscellaneous.areValuesDifferent(item.um_neg_amount_balance, item.balance) ? 'border_red' : ''}>
                            {item.um_neg_amount_balance ? <DisplayBoxRight>{item?.um_neg_amount_balance ? <>${formatCurrency(item?.um_neg_amount_balance)}</> : '-'}</DisplayBoxRight> : ''}                        
                        </td> : null}

                        {/* um negotiated amount paid  */}
                        <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}>
                            <DefaultUpdater type={'checkbox'} value={item.um_neg_paid} name={'um_neg_paid'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'um_neg_paid', type_id: item?.id, folder: 'practices'}} extraOptions={{setStyle: 'centered', isDisabled: !isUmSplitEnabled}}/>
                        </td>

                        {/* um negotiated amount received amount  */}
                        <td><DisplayBoxRight>{item?.um_neg_received_amount ? <>${formatCurrency(item?.um_neg_received_amount)}</> : '-'}</DisplayBoxRight></td>
                    </tr>
                ))}

                {/* //totals  */}
                <tr className={classes.totals_row}>
                    <td colSpan={2}>Totals</td>
                    <td><DisplayBoxRight>{totalCharges ? <>${formatCurrency(totalCharges)}</> : '-'}</DisplayBoxRight></td>
                    <td><DisplayBoxRight>{totalBalance ? <>${formatCurrency(totalBalance)}</> : '-'}</DisplayBoxRight></td>                
                    <td>
                        <InputBoxCurrency hasColorWhite={true} action={totalsNegotiatedAmountHandler} currentValue={roundCurrencyValue(totalLiaOfferedAmount)} name={'lia_neg_offered_amount_totals'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'lia_neg_offered_amount', type_id: null, folder: 'practices'}} textRight={true} fixWidth={150}/>
                    </td>
                    <td>
                        <InputBoxCurrency hasColorWhite={true} action={totalsNegotiatedAmountHandler} currentValue={roundCurrencyValue(totalLiaCounterOffer)} name={'lia_neg_counter_offer_totals'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'lia_neg_counter_offer', type_id: null, folder: 'practices'}} textRight={true} fixWidth={150}/>
                    </td>
                    <td>
                        <InputBoxCurrency hasColorWhite={true} action={totalsNegotiatedAmountHandler} currentValue={roundCurrencyValue(totalLiaNegAmount)} name={'lia_neg_amount_totals'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'lia_neg_amount', type_id: null, folder: 'practices'}} textRight={true} fixWidth={150}/>
                    </td>
                    <td>
                        <DisplayBoxRight>
                            {formatPercentageOrNull(totalCharges, totalLiaPctBaseAmount) ? <>{formatPercentageOrNull(totalCharges, totalLiaPctBaseAmount)}%</> : '-'}
                        </DisplayBoxRight>
                    </td>
                    {hasLiaNegBalance ? <td></td> : null}

                    {/* <td><DisplayBoxRight>{makeTotals(practices, 'lia_neg_amount_balance') ? <>${formatCurrency(makeTotals(practices, 'lia_neg_amount_balance'))}</> : '-'}</DisplayBoxRight></td> */}
                    <td></td>
                    <td><DisplayBoxRight>{totalLiaReceived ? <>${formatCurrency(totalLiaReceived)}</> : '-'}</DisplayBoxRight></td>
                    <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}>
                        <InputBoxCurrency hasColorWhite={true} action={totalsNegotiatedAmountHandler} currentValue={roundCurrencyValue(totalUmOfferedAmount)} name={'um_neg_offered_amount_totals'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'um_neg_offered_amount', type_id: null, folder: 'practices'}} textRight={true} fixWidth={150} isNotModifiable={!isUmSplitEnabled}/>
                    </td>
                    <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}>
                        <InputBoxCurrency hasColorWhite={true} action={totalsNegotiatedAmountHandler} currentValue={roundCurrencyValue(totalUmCounterOffer)} name={'um_neg_counter_offer_totals'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'um_neg_counter_offer', type_id: null, folder: 'practices'}} textRight={true} fixWidth={150} isNotModifiable={!isUmSplitEnabled}/>
                    </td>
                    <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}>
                        <InputBoxCurrency hasColorWhite={true} action={totalsNegotiatedAmountHandler} currentValue={roundCurrencyValue(totalUmNegAmount)} name={'um_neg_amount_totals'} identifier={{type: 'case_practice', permissionType: 'case_negotiation', field: 'um_neg_amount', type_id: null, folder: 'practices'}} textRight={true} fixWidth={150} isNotModifiable={!isUmSplitEnabled}/>
                    </td>
                    <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}>
                        <DisplayBoxRight>
                            {formatPercentageOrNull(totalCharges, totalUmPctBaseAmount) ? <>{formatPercentageOrNull(totalCharges, totalUmPctBaseAmount)}%</> : '-'}
                        </DisplayBoxRight>
                    </td>
                    {hasUmNegBalance ? <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}></td> : null}   
                    <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}></td>       
                    <td className={!isUmSplitEnabled ? classes.disabled_cell : ''}><DisplayBoxRight>{totalUmReceived ? <>${formatCurrency(totalUmReceived)}</> : '-'}</DisplayBoxRight></td>
                </tr>
            </tbody>
        </table>
    </>
}